<template>
  <div class="pa-4 px-md-6">
    <portal-page-header
      :title="portalScreenTitle || 'Mis datos'"
      :subtitle="portalScreenSubtitle"
    />

    <v-skeleton-loader v-if="loading" type="article, actions" class="mx-auto" />

    <div
      v-else-if="loadError"
      class="d-flex align-center justify-center text-body-2 text-medium-emphasis py-12 px-2"
    >
      No hay datos disponibles.
    </div>

    <v-card v-else class="portal-mis-datos__card mx-auto" rounded="lg" elevation="1">
      <v-card-text>
        <v-form ref="formRef" v-model="formValid" @submit.prevent="submit">
          <h2 class="text-primary text-subtitle-1 font-weight-bold mb-3">
            Datos personales
          </h2>
          <v-row dense>
            <v-col cols="12" md="2">
              <TipoDocumentoTextField
                v-model:tipo-id="docTipoId"
                v-model:numero="docNumero"
                readonly
                :rules-nro-doc="[]"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                :model-value="ficha.nombre"
                label="Nombre"
                variant="outlined"
                density="compact"
                readonly
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                :model-value="ficha.apellido"
                label="Apellido"
                variant="outlined"
                density="compact"
                readonly
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="2">
              <DatePicker
                v-model="form.fechaNacimiento"
                label="Fecha de nacimiento"
                :max-date="maxFechaNacimiento"
                hide-details="auto"
                readonly=""
              />
            </v-col>
            <v-col cols="6" md="1">
              <EdadTextField
                :edad="edadDesdeFecha"
                readonly-prop
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" md="1">
              <v-text-field
                :model-value="sexoLabel"
                label="Sexo"
                variant="outlined"
                density="compact"
                readonly
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <v-divider class="mt-4 mb-2" :opacity="0.20" />

          <h2 class="text-primary text-subtitle-1 font-weight-bold mb-3">
            Datos de contacto
          </h2>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.email"
                label="Correo electrónico"
                type="email"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :readonly="!permitirEdicionEmailFicha"
                :rules="emailRules"
              />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field
                v-model="form.celular"
                label="Celular"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @keypress="onOnlyNumbers"
              />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field
                v-model="form.telefono"
                label="Teléfono"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @keypress="onOnlyNumbers"
              />
            </v-col>
          </v-row>

          <v-divider class="mt-4 mb-2" :opacity="0.20" />

          <h2 class="text-primary text-subtitle-1 font-weight-bold mb-3">
            Datos demográficos
          </h2>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.ficCalle"
                label="Calle"
                variant="outlined"
                density="compact"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" md="1">
              <v-text-field
                v-model="form.ficNro"
                label="Número"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @keypress="onOnlyNumbers"
              />
            </v-col>
            <v-col cols="6" md="1">
              <v-text-field v-model="form.ficPiso" label="Piso" variant="outlined" density="compact" hide-details="auto" />
            </v-col>
            <v-col cols="6" md="1">
              <v-text-field
                v-model="form.ficDpto"
                label="Dpto."
                variant="outlined"
                density="compact"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" md="1">
              <v-text-field
                v-model="form.ficCpostal"
                label="Código postal"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @keypress="onOnlyNumbers"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="form.barId"
                :items="barriosTaxonomia"
                item-title="value"
                item-value="id"
                label="Barrio"
                variant="outlined"
                density="compact"
                hide-details="auto"
                clearable
                no-data-text="Sin resultados"
                @update:model-value="onBarrioTaxonomiaChange"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="form.locId"
                :items="localidadesTaxonomia"
                item-title="value"
                item-value="id"
                label="Localidad"
                variant="outlined"
                density="compact"
                hide-details="auto"
                clearable
                no-data-text="Sin resultados"
                @update:model-value="onLocalidadTaxonomiaChange"
              />
            </v-col>
          </v-row>

          <div class="d-flex gap-3 mt-4 justify-end">
            <v-spacer />
            <v-btn
              v-if="formTieneCambiosSinGuardar"
              variant="outlined"
              type="button"
              :disabled="saving"
              @click="reload"
              size="small"
            >
              Descartar cambios
            </v-btn>
            <v-btn
              type="submit"
              variant="elevated"
              color="primary"
              :disabled="saving || !formTieneCambiosSinGuardar || formValid === false"
              :loading="saving"
              size="small"
            >
              Guardar
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <ConfirmDialog
      v-model="leaveConfirmOpen"
      title="Cambios sin guardar"
      :message="leaveConfirmMessage"
      confirm-text="Salir sin guardar"
      cancel-text="Seguir editando"
      @confirm="onLeaveConfirm"
      @cancel="onLeaveCancel"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import PortalPageHeader from '../../../components/layout/PortalPageHeader.vue'
import rules from '../../../utils/helpers/rules'
import {
  computeAgeYearsFromIso,
  normalizeIsoDateString,
  todayIsoLocal,
} from '../../../utils/helpers/portalDateFormat'
import TipoDocumentoTextField from '../../../components/shared/TipoDocumentoTextField.vue'
import DatePicker from '../../../components/shared/DatePicker.vue'
import EdadTextField from '../../../components/shared/EdadTextField.vue'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog.vue'
import portalScreenSubtitleMixin from '../../../mixins/portalScreenSubtitle'

export default {
  name: 'PortalMisDatos',
  components: {
    TipoDocumentoTextField,
    DatePicker,
    EdadTextField,
    ConfirmDialog,
    PortalPageHeader,
  },
  mixins: [portalScreenSubtitleMixin],
  data() {
    return {
      docTipoId: null,
      docNumero: '',
      loading: true,
      saving: false,
      loadError: false,
      permitirEdicionEmailFicha: false,
      emailCuenta: '',
      ficha: {
        id: 0,
        tipoDoc: '',
        nroDoc: '',
        nombre: '',
        apellido: '',
        sexo: '',
        fechaNacimiento: '',
        telefono: '',
        celular: '',
        email: '',
        cuit: '',
        direccion: '',
        direccionNro: '',
        ficPiso: '',
        ficDpto: '',
        ficCpostal: '',
        locNombre: '',
        barrioNombre: '',
        tdId: null,
        locId: null,
        barId: null,
      },
      form: {
        sexo: '',
        fechaNacimiento: '',
        email: '',
        telefono: '',
        celular: '',
        ficCalle: '',
        ficNro: '',
        ficPiso: '',
        ficDpto: '',
        ficCpostal: '',
        locNombre: '',
        barrioNombre: '',
        locId: null,
        barId: null,
      },
      /** Evita limpiar barrio al rellenar el formulario desde la ficha. */
      aplicandoFichaForm: false,
      sexoItems: [
        { text: 'Masculino', value: 'M' },
        { text: 'Femenino', value: 'F' },
        { text: 'Otro / no informado', value: 'O' },
      ],
      rules,
      /** Copia normalizada del formulario al cargar / descartar / guardar ok (para detectar cambios). */
      formLineaBase: null,
      formValid: null,
      leaveConfirmOpen: false,
      leaveGuardRouteResolve: null,
      leaveConfirmMessage:
        'Tiene cambios sin guardar. Si sale de esta pantalla, se perderán. ¿Desea salir sin guardar?',
    }
  },
  computed: {
    ...mapState('taxonomy', ['localidadesTaxonomia', 'barriosTaxonomia']),
    ...mapState('portalHome', ['activePortalFichaId']),
    /** True si el formulario editable difiere de la última línea base (misma lógica que el payload). */
    formTieneCambiosSinGuardar() {
      if (this.formLineaBase == null) return false
      return this.serializeFormNormalizado(this.form) !== this.serializeFormNormalizado(this.formLineaBase)
    },
    maxFechaNacimiento() {
      return todayIsoLocal()
    },
    /** Años cumplidos según `form.fechaNacimiento` (ISO); vacío si no hay fecha válida. */
    edadDesdeFecha() {
      const y = computeAgeYearsFromIso(this.form.fechaNacimiento)
      return y === null ? '' : y
    },
    emailRules() {
      const r = [this.rules.email]
      if (this.permitirEdicionEmailFicha) r.unshift(this.rules.required)
      return r
    },
    /** Texto amigable del código de sexo que viene en la ficha / formulario. */
    sexoLabel() {
      const code = String(this.form.sexo ?? '').trim().toUpperCase()
      const it = this.sexoItems.find((x) => x.value === code)
      return it ? it.text : code
    },
  },
  watch: {
    '$route.query.verificacionEmail': {
      immediate: true,
      handler(v) {
        if (v === 'ok') {
          this.setAlert({
            type: 'success',
            message: 'Su correo electrónico fue verificado y actualizado correctamente.',
          })
          this.clearEmailQuery()
          this.load()
        } else if (v === 'error') {
          this.setAlert({
            type: 'warning',
            message:
              'No se pudo confirmar el cambio de correo. El enlace puede haber expirado o no ser válido. Intente guardar de nuevo sus datos.',
          })
          this.clearEmailQuery()
        }
      },
    },
    activePortalFichaId() {
      this.load()
    },
  },
  mounted() {
    this.load()
    this.fetchLocalidadesYBarriosTaxonomia()
  },
  beforeRouteLeave() {
    if (!this.formTieneCambiosSinGuardar) return true
    this.leaveConfirmOpen = true
    return new Promise((resolve) => {
      this.leaveGuardRouteResolve = resolve
    })
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      fetchLocalidadesYBarriosTaxonomia: 'taxonomy/fetchLocalidadesYBarriosTaxonomia',
      fetchMisDatos: 'misDatos/fetchMisDatos',
      saveMisDatos: 'misDatos/saveMisDatos',
    }),
    clearEmailQuery() {
      const q = { ...this.$route.query }
      delete q.verificacionEmail
      this.$router.replace({ path: this.$route.path, query: q })
    },
    onLeaveConfirm() {
      this.leaveConfirmOpen = false
      const routeResolve = this.leaveGuardRouteResolve
      this.leaveGuardRouteResolve = null
      if (routeResolve) routeResolve(true)
    },
    onLeaveCancel() {
      this.leaveConfirmOpen = false
      if (this.leaveGuardRouteResolve) {
        this.leaveGuardRouteResolve(false)
        this.leaveGuardRouteResolve = null
      }
    },
    /** Solo dígitos: bloquea teclas imprimibles no numéricas (no afecta Tab, flechas, etc.). */
    onOnlyNumbers(event) {
      const k = event.key
      if (k.length === 1 && !/[0-9]/.test(k)) {
        event.preventDefault()
      }
    },
    mapApiFicha(raw) {
      const r = raw || {}
      return {
        id: r.id ?? 0,
        tipoDoc: r.tipoDoc ?? '',
        nroDoc: r.nroDoc ?? '',
        nombre: r.nombre ?? '',
        apellido: r.apellido ?? '',
        sexo: r.sexo ?? '',
        fechaNacimiento: normalizeIsoDateString(r.fechaNacimiento ?? ''),
        telefono: r.telefono ?? '',
        celular: r.celular ?? '',
        email: r.email ?? '',
        cuit: r.cuit ?? '',
        direccion: r.direccion ?? '',
        direccionNro: r.direccionNro ?? '',
        ficPiso: r.ficPiso ?? '',
        ficDpto: r.ficDpto ?? '',
        ficCpostal: r.ficCpostal ?? '',
        locNombre: r.locNombre ?? '',
        barrioNombre: r.barrioNombre ?? '',
        tdId: r.tdId ?? null,
        locId: this.normalizarIdTaxonomiaDesdeApi(r.locId),
        barId: this.normalizarIdTaxonomiaDesdeApi(r.barId),
      }
    },
    normalizarIdTaxonomiaDesdeApi(v) {
      if (v == null || v === '') return null
      const n = Number(v)
      if (!Number.isFinite(n) || n <= 0) return null
      return n
    },
    applyFormFromFicha() {
      const f = this.ficha
      const td = f.tdId
      this.docTipoId =
        td != null && td !== '' && Number.isFinite(Number(td)) ? Number(td) : null
      this.docNumero = String(f.nroDoc ?? '')
      this.aplicandoFichaForm = true
      this.form = {
        sexo: f.sexo || 'O',
        fechaNacimiento: f.fechaNacimiento || '',
        email: this.emailCuenta || f.email || '',
        telefono: f.telefono || '',
        celular: f.celular || '',
        ficCalle: f.direccion || '',
        ficNro: f.direccionNro || '',
        ficPiso: f.ficPiso || '',
        ficDpto: f.ficDpto || '',
        ficCpostal: f.ficCpostal || '',
        locNombre: f.locNombre || '',
        barrioNombre: f.barrioNombre || '',
        locId: this.normalizarIdTaxonomiaDesdeApi(f.locId),
        barId: this.normalizarIdTaxonomiaDesdeApi(f.barId),
      }
      this.$nextTick(() => {
        this.aplicandoFichaForm = false
        this.actualizarLineaBaseFormulario()
      })
    },
    onLocalidadTaxonomiaChange(locId) {
      const item = this.localidadesTaxonomia.find((i) => i.id === locId)
      if (item) this.form.locNombre = item.value
      else if (locId == null || locId === '') this.form.locNombre = ''
      if (this.aplicandoFichaForm) return
      this.form.barId = null
      this.form.barrioNombre = ''
    },
    onBarrioTaxonomiaChange(barId) {
      const item = this.barriosTaxonomia.find((i) => i.id === barId)
      if (item) this.form.barrioNombre = item.value
      else if (barId == null || barId === '') this.form.barrioNombre = ''
    },
    /** Objeto comparable (solo strings normalizados). */
    normalizarFormularioParaComparar(src) {
      const s = src || {}
      return {
        sexo: String(s.sexo ?? ''),
        fechaNacimiento: normalizeIsoDateString(s.fechaNacimiento ?? ''),
        email: String(s.email ?? '').trim(),
        telefono: String(s.telefono ?? '').trim(),
        celular: String(s.celular ?? '').trim(),
        ficCalle: String(s.ficCalle ?? '').trim(),
        ficNro: String(s.ficNro ?? '').trim(),
        ficPiso: String(s.ficPiso ?? '').trim(),
        ficDpto: String(s.ficDpto ?? '').trim(),
        ficCpostal: String(s.ficCpostal ?? '').trim(),
        locNombre: String(s.locNombre ?? '').trim(),
        barrioNombre: String(s.barrioNombre ?? '').trim(),
        locId: s.locId == null || s.locId === '' ? '' : String(Number(s.locId)),
        barId: s.barId == null || s.barId === '' ? '' : String(Number(s.barId)),
      }
    },
    serializeFormNormalizado(src) {
      return JSON.stringify(this.normalizarFormularioParaComparar(src))
    },
    actualizarLineaBaseFormulario() {
      this.formLineaBase = this.normalizarFormularioParaComparar(this.form)
    },
    async load() {
      this.loading = true
      this.loadError = false
      try {
        const data = await this.fetchMisDatos()
        this.ficha = this.mapApiFicha(data.ficha)
        this.permitirEdicionEmailFicha = !!data.permitirEdicionEmailFicha
        this.emailCuenta = String(data.emailCuenta ?? '').trim()
        this.applyFormFromFicha()
      } catch {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    reload() {
      this.applyFormFromFicha()
    },
    buildPayload() {
      const f = this.ficha
      const fecha = String(this.form.fechaNacimiento || '').trim()
      return {
        fichaId: f.id,
        ficTipodoc: f.tdId,
        ficNrodoc: f.nroDoc,
        ficNombre: f.nombre,
        ficApellido: f.apellido,
        pacienteHabilitado: false,
        habilitadoPortalPaciente: false,
        fechaNacimiento: fecha === '' ? null : fecha,
        sexo: this.form.sexo || null,
        email: String(this.form.email || '').trim(),
        telefono: this.form.telefono || null,
        celular: this.form.celular || null,
        ficCalle: this.form.ficCalle || null,
        ficNro: this.form.ficNro || null,
        ficPiso: this.form.ficPiso || null,
        ficDpto: this.form.ficDpto || null,
        ficCpostal: this.form.ficCpostal || null,
        locNombre: String(this.form.locNombre || '').trim() || null,
        barrioNombre: String(this.form.barrioNombre || '').trim() || null,
        locId: this.form.locId != null && this.form.locId !== '' ? Number(this.form.locId) : null,
        barId: this.form.barId != null && this.form.barId !== '' ? Number(this.form.barId) : null,
      }
    },
    async submit() {
      if (!this.formTieneCambiosSinGuardar) return
      const { valid } = await this.$refs.formRef.validate()
      if (!valid) return
      this.saving = true
      try {
        const data = await this.saveMisDatos(this.buildPayload())
        if (data && data.pendingEmailConfirmation) {
          this.setAlert({
            type: 'success',
            message:
              'Datos actualizados. Le enviamos un correo al nuevo e-mail para que confirme el cambio. Revise su bandeja de entrada.',
          })
          await this.load()
          return
        }
        this.setAlert({ type: 'success', message: 'Sus datos se guardaron correctamente.' })
        await this.load()
      } catch (error) {
        console.error('Error al guardar los datos', error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.portal-mis-datos__card {
  background-color: rgb(var(--v-theme-surface));
}
.portal-mis-datos__section-card :deep(.v-field__outline) {
  --v-field-border-opacity: 0.38;
}
.gap-3 {
  gap: 0.75rem;
}
</style>
